import React, {Fragment, useEffect, useMemo, useState} from 'react'
import 'chart.js/auto';
import {Line} from "react-chartjs-2";
import axios from "axios";
import moment from "moment";
import {useHistory} from "react-router-dom";
import {ClipLoader} from "react-spinners";
import {
    CustomContainer,
    CustomHeader,
    CustomTD,
    RequiredItemsBadge,
    StyledTable
} from "@pages/new-design-components/custom-components";
import {LoaderContainer, override} from "@pages/reusable-components/styled-components";
import Picker from "@pages/components/DatePicker";
import {API_CORE_URL} from "@constants/index";
import notify from '@utils/notify';

const COMPARE_OPTIONS = [
    {key: 'yesterday', label: 'Yesterday'},
    {key: '7', label: '7 days ago', days: 7},
    {key: '30', label: '30 days ago', days: 30},
    {key: 'custom', label: 'Custom date'},
];

const RANGE_OPTIONS = [
    {key: 30, label: '30 days'},
    {key: 90, label: '90 days'},
    {key: 365, label: '365 days'},
];

const LINE_COLORS = [
    '#3080EB', '#F3994A', '#8e44ad', '#219852', '#EB5757', '#192d91',
    '#f34abd', '#cfa076', '#2f9119', '#8c035e', '#00a3a3', '#869191',
];

const GOOD_COLOR = '#219852';
const BAD_COLOR = '#EB5757';

const NEUTRAL_COLOR = '#869191';

// The API sends amounts as 2-decimal strings ("2430.00"); always convert before maths or comparisons.
const toAmount = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const gbp = new Intl.NumberFormat('en-GB', {style: 'currency', currency: 'GBP'});
const formatMoney = (value) => gbp.format(toAmount(value));

// Whole-number tenths per row, distributed by largest remainder so the column sums to exactly 100.0%.
const percentagesOf = (amounts, total) => {
    if (total <= 0) return amounts.map(() => '0.0');
    const raw = amounts.map(amount => amount / total * 1000);
    const floored = raw.map(Math.floor);
    let remainder = 1000 - floored.reduce((sum, value) => sum + value, 0);
    raw.map((value, index) => ({index, fraction: value - floored[index]}))
        .sort((a, b) => b.fraction - a.fraction)
        .forEach(({index}) => {
            if (remainder > 0) {
                floored[index] += 1;
                remainder -= 1;
            }
        });
    return floored.map(tenths => (tenths / 10).toFixed(1));
};

function ChangeCell({change}) {
    // null means no earlier snapshot: show a dash, don't treat it as 0.
    if (change === null || change === undefined) return <>–</>;
    const amount = toAmount(change);
    if (amount === 0) return <span style={{color: NEUTRAL_COLOR}}>{formatMoney(0)}</span>;
    const isUp = amount > 0;
    return (
        <span style={{color: isUp ? BAD_COLOR : GOOD_COLOR, fontWeight: 600}}>
            {isUp ? '▲ +' : '▼ −'}{formatMoney(Math.abs(amount))}
        </span>
    );
}

export default function OwedByInvoiceType() {
    const history = useHistory();

    const [compareKey, setCompareKey] = useState('yesterday');
    const [customDate, setCustomDate] = useState('');
    const [totals, setTotals] = useState(null);
    const [totalsLoading, setTotalsLoading] = useState(true);
    const [totalsError, setTotalsError] = useState(false);

    const [rangeDays, setRangeDays] = useState(90);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [historyError, setHistoryError] = useState(false);
    const [showTotalLine, setShowTotalLine] = useState(true);

    const compareTo = useMemo(() => {
        if (compareKey === 'custom') return customDate || null;
        const option = COMPARE_OPTIONS.find(o => o.key === compareKey);
        return option?.days ? moment().subtract(option.days, 'days').format('YYYY-MM-DD') : null;
    }, [compareKey, customDate]);

    useEffect(() => {
        // Custom selected but no date picked yet: keep showing the previous result.
        if (compareKey === 'custom' && !customDate) return;
        setTotalsLoading(true);
        setTotalsError(false);
        axios({
            method: 'GET',
            url: `${API_CORE_URL}/debt-totals-by-type`,
            params: compareTo ? {compare_to: compareTo} : {},
        }).then(res => {
            // axios `data`, then the API's { errors, data, message } wrapper.
            setTotals(res.data?.data ?? null);
        }).catch(() => {
            setTotalsError(true);
            notify.warning('Something went wrong while getting invoice type totals!');
        }).finally(() => {
            setTotalsLoading(false);
        });
    }, [compareTo]);

    useEffect(() => {
        setHistoryLoading(true);
        setHistoryError(false);
        axios({
            method: 'GET',
            url: `${API_CORE_URL}/debt-totals-by-type/history`,
            params: {
                from: moment().subtract(rangeDays, 'days').format('YYYY-MM-DD'),
                to: moment().format('YYYY-MM-DD'),
            },
        }).then(res => {
            setHistoryData(res.data?.data ?? null);
        }).catch(() => {
            setHistoryError(true);
            notify.warning('Something went wrong while getting invoice type history!');
        }).finally(() => {
            setHistoryLoading(false);
        });
    }, [rangeDays]);

    const trendChart = useMemo(() => {
        const rows = historyData?.history || [];
        // A single snapshot isn't a trend yet; show the empty state instead.
        if (rows.length < 2) return null;

        const labels = historyData?.labels || {};
        // Include every type seen in any snapshot, even if absent from `labels`.
        const typeKeys = Array.from(new Set([
            ...Object.keys(labels),
            ...rows.flatMap(row => Object.keys(row.types || {})),
        ]));

        const datasets = typeKeys.map((type, index) => ({
            label: labels[type] || type,
            data: rows.map(row => toAmount(row.types?.[type])),
            borderColor: LINE_COLORS[index % LINE_COLORS.length],
            backgroundColor: LINE_COLORS[index % LINE_COLORS.length],
            borderWidth: 2,
            tension: 0.3,
            pointRadius: rows.length > 60 ? 0 : 2,
        }));

        if (showTotalLine) {
            datasets.unshift({
                label: 'Total',
                data: rows.map(row => toAmount(row.total_overdue_amount)),
                borderColor: '#000000',
                backgroundColor: '#000000',
                borderWidth: 2,
                borderDash: [6, 4],
                tension: 0.3,
                pointRadius: rows.length > 60 ? 0 : 2,
            });
        }

        const chartData = {labels: rows.map(row => row.date), datasets};
        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {mode: 'index', intersect: false},
            plugins: {
                legend: {position: 'top', labels: {usePointStyle: true}},
                tooltip: {
                    callbacks: {
                        label: (item) => `${item.dataset.label}: ${formatMoney(item.raw)}`,
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {callback: (value) => `£${Number(value).toLocaleString('en-GB')}`},
                },
            },
        };
        return <Line data={chartData} options={chartOptions}/>;
    }, [historyData, showTotalLine]);

    const types = Array.isArray(totals?.types) ? totals.types : [];
    const percentages = useMemo(
        () => percentagesOf(types.map(type => toAmount(type.overdue_amount)), toAmount(totals?.total_overdue_amount)),
        [totals]
    );

    const headerCell = (label) => (
        <CustomTD border={'none'} radius={'0px'} borderBottom={'1px solid #C0D9FF'}>
            <CustomHeader lineHeight={'none'} fontSize={'14px'} weight={'600'} color={'#0000008C'}>
                {label}
            </CustomHeader>
        </CustomTD>
    );

    const loader = (isLoading) => (
        <LoaderContainer>
            <ClipLoader css={override} size={40} color={`#7B61E4`} loading={isLoading}/>
        </LoaderContainer>
    );

    return (
        <CustomContainer backgroundColor={'#FFF'} borderRadius="16px" padding="16px" className={'mb-2'}>
            <CustomHeader fontSize={'20px'} weight={'600'} lineHeight={'unset'}>Owed by Invoice Type</CustomHeader>

            <div className={'d-flex g-5 mb-3 flex-wrap align-items-center'}>
                <CustomHeader fontSize={'14px'} weight={'500'} lineHeight={'unset'}>Compare to:</CustomHeader>
                {COMPARE_OPTIONS.map(option => (
                    <Fragment key={option.key}>
                        <RequiredItemsBadge
                            backgroundColor={'#F2F2F2'}
                            type={option.key}
                            active={compareKey === option.key}
                            onClick={() => setCompareKey(option.key)}
                        >
                            {option.label}
                        </RequiredItemsBadge>
                    </Fragment>
                ))}
                {compareKey === 'custom' && (
                    <Picker
                        value={customDate}
                        placeholderText={'Compare date'}
                        onChange={(date) => setCustomDate(date)}
                    />
                )}
            </div>

            {totalsLoading ? loader(totalsLoading) : totalsError && !totals ? (
                <CustomHeader fontSize={'14px'} weight={'400'} color={BAD_COLOR}>
                    Could not load invoice type totals. Please try again later.
                </CustomHeader>
            ) : totals && (
                <>
                    <div className={'d-flex flex-wrap g-5 mb-3'}>
                        <CustomContainer backgroundColor={'#F7F9FC'} borderRadius="12px" padding="12px">
                            <CustomHeader fontSize={'13px'} weight={'500'} color={'#0000008C'}>Total overdue</CustomHeader>
                            <CustomHeader fontSize={'22px'} weight={'700'} color={BAD_COLOR}>
                                {formatMoney(totals.total_overdue_amount)}
                            </CustomHeader>
                        </CustomContainer>
                        <CustomContainer backgroundColor={'#F7F9FC'} borderRadius="12px" padding="12px">
                            <CustomHeader fontSize={'13px'} weight={'500'} color={'#0000008C'}>Overdue invoices</CustomHeader>
                            <CustomHeader fontSize={'22px'} weight={'700'}>
                                {totals.total_overdue_invoices ?? 0}
                            </CustomHeader>
                        </CustomContainer>
                        <div className={'d-flex align-items-end'}>
                            <CustomHeader fontSize={'13px'} weight={'400'} color={'#0000008C'}>
                                {totals.compared_to ? `compared to ${totals.compared_to}` : 'no earlier snapshot to compare with'}
                            </CustomHeader>
                        </div>
                    </div>

                    <div className={'table-container'}>
                        <StyledTable>
                            <tbody>
                            <tr>
                                {headerCell('Invoice Type')}
                                {headerCell('Overdue Amount')}
                                {headerCell('Invoices')}
                                {headerCell('Customers')}
                                {headerCell('Change')}
                                {headerCell('% of Total')}
                            </tr>
                            {types.length === 0 && (
                                <tr>
                                    <CustomTD colSpan={6} className={'text-center'}>Nothing is overdue.</CustomTD>
                                </tr>
                            )}
                            {types.map((type, index) => {
                                return (
                                    <tr
                                        key={type.invoice_type}
                                        className={'cursor-pointer'}
                                        title={`Open ${type.label} customers`}
                                        onClick={() => history.push({
                                            pathname: '/admin/debt-customers',
                                            search: new URLSearchParams({invoice_type: type.invoice_type}).toString(),
                                            // Label for the filter chip; the customers page falls back to the raw value.
                                            state: {invoiceTypeLabel: type.label},
                                        })}
                                    >
                                        <CustomTD color={'#3F8CFF'}>{type.label}</CustomTD>
                                        <CustomTD color={'#EB5855'}>{formatMoney(type.overdue_amount)}</CustomTD>
                                        <CustomTD>{type.overdue_invoices ?? 0}</CustomTD>
                                        <CustomTD>{type.customers ?? 0}</CustomTD>
                                        <CustomTD><ChangeCell change={type.change}/></CustomTD>
                                        <CustomTD>{percentages[index]}%</CustomTD>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </StyledTable>
                    </div>
                </>
            )}

            <div className={'d-flex g-5 mt-4 mb-3 flex-wrap align-items-center'}>
                <CustomHeader fontSize={'16px'} weight={'600'} lineHeight={'unset'}>Trend</CustomHeader>
                {RANGE_OPTIONS.map(option => (
                    <Fragment key={option.key}>
                        <RequiredItemsBadge
                            backgroundColor={'#F2F2F2'}
                            type={String(option.key)}
                            active={rangeDays === option.key}
                            onClick={() => setRangeDays(option.key)}
                        >
                            {option.label}
                        </RequiredItemsBadge>
                    </Fragment>
                ))}
                <label className={'d-flex align-items-center g-5 mb-0 cursor-pointer'}>
                    <input
                        type={'checkbox'}
                        checked={showTotalLine}
                        onChange={(e) => setShowTotalLine(e.target.checked)}
                    />
                    Show total
                </label>
            </div>

            {historyLoading ? loader(historyLoading) : historyError ? (
                <CustomHeader fontSize={'14px'} weight={'400'} color={BAD_COLOR}>
                    Could not load trend data. Please try again later.
                </CustomHeader>
            ) : trendChart ? (
                <div style={{position: 'relative', height: '360px'}}>{trendChart}</div>
            ) : (
                <CustomHeader fontSize={'14px'} weight={'400'} color={'#0000008C'} className={'text-center py-4'}>
                    Trend data builds up daily. Check back tomorrow to see how overdue amounts change over time.
                </CustomHeader>
            )}
        </CustomContainer>
    );
}
